import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiRefreshCw, FiSearch, FiDollarSign, FiAlertCircle,
  FiCheckCircle, FiClock, FiTrendingDown, FiFileText, FiDownload,
  FiChevronDown, FiChevronRight
} from 'react-icons/fi';
import { getCxCSummary, getCxCList, getCxCById, cobrarCxC, getClients, getEmpresaInfo } from '../../../services/api';
import { exportPDF, exportExcel } from '../../../utils/exportCxC';
import ClienteFiltroCombo from '../shared/ClienteFiltroCombo';
import '../../../styles/ModuleTable.css';
import '../../../styles/ComprasMaintenance.css';
import '../Movements/MovimientosMaintenance.css';

// ── helpers ────────────────────────────────────────────────
const fmtS  = n  => `S/ ${parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = s => s ? new Date(s).toLocaleDateString('es-PE', { timeZone: 'America/Lima' }) : '—';
// due_date es DATE pura (sin hora, medianoche UTC) — timeZone la correría un
// día atrás; se extrae el día calendario directo del string ISO, sin Date.
const fmtDateOnly = s => { if (!s) return '—'; const m = String(s).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s).slice(0, 10); };

const PAYMENT_METHODS = [
  { id: 1, name: 'EFECTIVO' },
  { id: 3, name: 'TARJETA CRED. O DEBITO' },
  { id: 4, name: 'TRANSFERENCIA BANCARIA' },
  { id: 5, name: 'PLIN' },
  { id: 6, name: 'YAPE' }
];

// ── KPI card ───────────────────────────────────────────────
function KpiCard({ icon, label, value, color, sub }) {
  const colors = {
    blue:   { bg: '#eff6ff', border: '#bfdbfe', icon: '#3b82f6' },
    orange: { bg: '#fff7ed', border: '#fed7aa', icon: '#d97706' },
    red:    { bg: '#fef2f2', border: '#fecaca', icon: '#dc2626' },
    green:  { bg: '#f0fdf4', border: '#bbf7d0', icon: '#16a34a' }
  };
  const c = colors[color] || colors.blue;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 16px', borderRadius: 8,
      background: c.bg, border: `1px solid ${c.border}`
    }}>
      <div style={{ fontSize: 22, color: c.icon, flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: 10.5, color: '#64748b', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: c.icon, fontWeight: 600, marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Badge de estado ────────────────────────────────────────
function BadgeCxC({ status, vencido }) {
  if (status === 'cancelled') {
    return <span className="badge-purchase-status" style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1' }}>ANULADA</span>;
  }
  if (vencido && status !== 'paid') {
    return <span className="badge-purchase-status badge-cancelled">VENCIDO</span>;
  }
  const cfg = {
    pending: { cls: 'badge-purchase-credit',  label: 'PENDIENTE' },
    partial: { cls: 'badge-purchase-partial', label: 'PARCIAL'   },
    paid:    { cls: 'badge-purchase-paid',    label: 'COBRADO'   }
  };
  const s = cfg[status] || cfg.pending;
  return <span className={`badge-purchase-status ${s.cls}`}>{s.label}</span>;
}

// ── Cabecera + filas de una tabla anidada dentro de un grupo (por cliente) ──
const CXC_ROWS_HEAD = (
  <tr className="mt-head-row">
    <th>Fecha</th>
    <th>Documento</th>
    <th style={{ textAlign: 'center' }}>Cuota</th>
    <th style={{ textAlign: 'right' }}>Monto</th>
    <th style={{ textAlign: 'right' }}>Cobrado</th>
    <th style={{ textAlign: 'right' }}>Restante</th>
    <th style={{ paddingLeft: 24 }}>Vcto.</th>
    <th style={{ paddingLeft: 24 }}>Estado</th>
    <th></th>
  </tr>
);

function CxCRows({ rows, onCobrar }) {
  return rows.map((r, i) => {
    const isVencido = !!r.vencido && r.status !== 'paid';
    const rowCls = r.status === 'paid'
      ? (i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd')
      : isVencido
        ? ''
        : (i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd');

    return (
      <tr key={r.id} className={rowCls}
        style={isVencido ? { background: '#fff5f5' } : {}}>
        <td style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>
          {fmtDate(r.sale_date || r.created_at)}
        </td>
        <td style={{ fontSize: 11.5, color: '#475569' }}>
          {r.document_type}
          {r.serie && (
            <span style={{ fontFamily: 'monospace', marginLeft: 4, fontSize: 11 }}>
              {r.serie}-{r.sequence}
            </span>
          )}
        </td>
        <td style={{ textAlign: 'center' }}>
          <span style={{
            background: '#0ea5e9', color: '#fff', fontWeight: 700, fontSize: 10.5,
            padding: '2px 10px', borderRadius: 20, whiteSpace: 'nowrap'
          }}>
            CUOTA {r.cuota_num}
          </span>
        </td>
        <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 12.5 }}>
          {fmtS(r.amount)}
        </td>
        <td style={{ textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>
          {fmtS(r.paid_amount)}
        </td>
        <td style={{
          textAlign: 'right', fontWeight: 700,
          color: parseFloat(r.restante) > 0 ? '#dc2626' : '#16a34a',
          fontSize: 13
        }}>
          {fmtS(r.restante)}
        </td>
        <td style={{
          whiteSpace: 'nowrap', fontSize: 11.5, paddingLeft: 24,
          color: isVencido ? '#dc2626' : '#64748b',
          fontWeight: isVencido ? 700 : 400
        }}>
          {fmtDateOnly(r.due_date)}
        </td>
        <td style={{ paddingLeft: 24 }}><BadgeCxC status={r.status} vencido={isVencido} /></td>
        <td className="td-actions">
          {r.status !== 'paid' && r.status !== 'cancelled' && (
            <button
              className="btn-modificar"
              style={{ padding: '4px 12px', fontSize: 11.5, display: 'flex', alignItems: 'center', gap: 4 }}
              onClick={() => onCobrar(r.id)}
            >
              <FiDollarSign size={12} /> Cobrar
            </button>
          )}
          {r.status === 'paid' && (
            <button
              className="btn-salir"
              style={{ padding: '4px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
              onClick={() => onCobrar(r.id)}
            >
              Ver historial
            </button>
          )}
        </td>
      </tr>
    );
  });
}

// ════════════════════════════════════════════════════════════
// MODAL DE COBRO
// ════════════════════════════════════════════════════════════
function ModalCobro({ arId, businessId, onClose, onCobrado }) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const [pmId,    setPmId]    = useState(1);
  const [monto,   setMonto]   = useState('');
  const [notas,   setNotas]   = useState('');

  useEffect(() => {
    getCxCById(arId, businessId)
      .then(res => {
        setDetail(res.data?.data);
        setMonto(parseFloat(res.data?.data?.restante || 0).toFixed(2));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [arId, businessId]);

  const handleCobrar = async () => {
    setError('');
    const pm = PAYMENT_METHODS.find(p => p.id === parseInt(pmId));
    setSaving(true);
    try {
      await cobrarCxC(arId, businessId, {
        amount:               parseFloat(monto),
        payment_method_id:    parseInt(pmId),
        payment_method_name:  pm?.name || 'EFECTIVO',
        notes:                notas || null
      });
      onCobrado();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  const restante = detail ? parseFloat(detail.restante) : 0;

  return (
    <div className="mt-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="mt-modal" style={{ maxWidth: 620 }}>
        <div className="mt-modal-header">
          <span>REGISTRAR COBRO / AMORTIZACIÓN</span>
          <button className="btn-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mt-modal-body">
          {loading ? (
            <div className="mt-loading">Cargando...</div>
          ) : !detail ? (
            <div style={{ color: '#dc2626' }}>No se pudo cargar el registro.</div>
          ) : (
            <>
              {/* Info del registro */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px',
                background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 12.5
              }}>
                <div>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>CLIENTE</span>
                  <div style={{ fontWeight: 700, color: '#1e293b' }}>{detail.client_name}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{detail.client_document}</div>
                </div>
                <div>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>MOTIVO</span>
                  <div style={{ fontWeight: 600, color: '#0369a1', fontFamily: 'monospace' }}>
                    {detail.sale_number || `CxC-${arId}`}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    {detail.document_type} {detail.serie ? `${detail.serie}-${detail.sequence}` : ''}
                  </div>
                </div>
                <div>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>NRO. CUOTA</span>
                  <div>
                    <span style={{
                      background: '#0ea5e9', color: '#fff', fontWeight: 700, fontSize: 11,
                      padding: '2px 10px', borderRadius: 20, display: 'inline-block'
                    }}>
                      CUOTA {detail.cuota_num}
                    </span>
                  </div>
                </div>
                <div>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>ESTADO</span>
                  <div>
                    <BadgeCxC status={detail.status} vencido={!!detail.vencido} />
                    {detail.due_date && (
                      <div style={{ fontSize: 10.5, color: detail.vencido ? '#dc2626' : '#64748b', marginTop: 2 }}>
                        Vcto: {fmtDateOnly(detail.due_date)}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>MONTO CUOTA</span>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{fmtS(detail.amount)}</div>
                </div>
                <div>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>YA COBRADO</span>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#16a34a' }}>{fmtS(detail.paid_amount)}</div>
                </div>
                <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>MONTO RESTANTE</span>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#dc2626' }}>{fmtS(restante)}</div>
                </div>
              </div>

              {/* Formulario de cobro */}
              {detail.status !== 'paid' ? (
                <div className="mt-form-grid">
                  <div className="mt-field">
                    <label>Forma de Pago <span className="req">*</span></label>
                    <select value={pmId} onChange={e => setPmId(e.target.value)}>
                      {PAYMENT_METHODS.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-field">
                    <label>Monto Abono <span className="req">*</span></label>
                    <input type="number" min="0.01" step="0.01" max={restante}
                      value={monto} onChange={e => setMonto(e.target.value)} />
                  </div>
                  <div className="mt-field mt-full">
                    <label>Notas / Referencia</label>
                    <input type="text" maxLength={200}
                      value={notas} onChange={e => setNotas(e.target.value)}
                      placeholder="Ej: Transferencia banco BCP, Voucher #1234..." />
                  </div>
                  {/* Restante después del abono */}
                  <div className="mt-field mt-full">
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      background: '#fef9c3', border: '1px solid #fde047',
                      borderRadius: 6, padding: '8px 12px', fontSize: 12.5
                    }}>
                      <span style={{ fontWeight: 600, color: '#78350f' }}>Restante después del cobro:</span>
                      <span style={{ fontWeight: 800, color: '#dc2626' }}>
                        {fmtS(Math.max(0, restante - parseFloat(monto || 0)))}
                      </span>
                    </div>
                  </div>
                  {error && (
                    <div className="mt-field mt-full" style={{
                      background: '#fef2f2', border: '1px solid #fecaca',
                      borderRadius: 6, padding: '8px 12px', color: '#dc2626', fontSize: 12.5, fontWeight: 600
                    }}>
                      ⚠ {error}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  background: '#f0fdf4', border: '1px solid #bbf7d0',
                  borderRadius: 6, padding: '12px 16px', color: '#16a34a',
                  fontWeight: 700, textAlign: 'center', fontSize: 13
                }}>
                  ✓ Esta deuda está completamente cobrada.
                </div>
              )}

              {/* Historial de cobros */}
              {detail.payments?.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: '#64748b',
                    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6
                  }}>
                    Historial de Cobros
                  </div>
                  <table className="mt-table" style={{ fontSize: 12 }}>
                    <thead>
                      <tr className="mt-head-row">
                        <th>#</th>
                        <th>Fecha</th>
                        <th>Forma de Pago</th>
                        <th style={{ textAlign: 'right' }}>Monto Cobrado</th>
                        <th>Notas</th>
                        <th>Usuario</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.payments.map((p, i) => (
                        <tr key={p.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                          <td style={{ color: '#94a3b8', fontSize: 11 }}>{i + 1}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(p.paid_at)}</td>
                          <td>
                            <span className="badge-purchase-status badge-purchase-paid">
                              {p.payment_method_short || p.payment_method_name || '—'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>
                            {fmtS(p.amount)}
                          </td>
                          <td style={{ fontSize: 11.5, color: '#64748b' }}>{p.notes || '—'}</td>
                          <td style={{ fontSize: 11.5 }}>{p.username || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
        <div className="mt-modal-footer">
          {detail && detail.status !== 'paid' && (
            <button className="btn-modificar" onClick={handleCobrar} disabled={saving || !monto}>
              {saving ? 'Registrando...' : `Registrar Cobro de ${fmtS(monto || 0)}`}
            </button>
          )}
          <button className="btn-salir" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════
export default function CuentasCobrarMaintenance({ businessId }) {
  const [summary,   setSummary]   = useState(null);
  const [rows,      setRows]      = useState([]);
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(false);
  const [clients,   setClients]   = useState([]);
  const [cobroModal, setCobroModal] = useState(null); // ar id

  const [filters, setFilters] = useState({
    client_id: '', status: '', from_date: '', to_date: '',
    search: '', overdue_only: false
  });
  const [searchText,    setSearchText]    = useState('');
  const [page,          setPage]          = useState(1);
  const [perPage,       setPerPage]       = useState(50);
  const [exporting,     setExporting]     = useState(false);
  const [appliedDates,  setAppliedDates]  = useState({ from_date: '', to_date: '' });

  // ── Agrupación por cliente — expandida por defecto, colapsable por grupo ──
  const [expandedClients, setExpandedClients] = useState(new Set());
  const toggleClient = (key) => setExpandedClients(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const groups = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      const key = r.client_id || 'sin-cliente';
      if (!map.has(key)) {
        map.set(key, {
          key,
          client_name:     r.client_name || 'CLIENTES VARIOS',
          client_document: r.client_document || '—',
          rows: [],
        });
      }
      map.get(key).rows.push(r);
    });
    return Array.from(map.values())
      .map(g => {
        const activos = g.rows.filter(r => r.status !== 'cancelled');
        return {
          ...g,
          count:          activos.length,
          total_monto:    activos.reduce((s, r) => s + parseFloat(r.amount || 0), 0),
          total_cobrado:  activos.reduce((s, r) => s + parseFloat(r.paid_amount || 0), 0),
          total_restante: activos.filter(r => r.status !== 'paid').reduce((s, r) => s + parseFloat(r.restante || 0), 0),
        };
      })
      .sort((a, b) => a.client_name.localeCompare(b.client_name));
  }, [rows]);

  useEffect(() => {
    setExpandedClients(new Set(groups.map(g => g.key)));
  }, [groups]);

  const loadSummary = useCallback(async (dates = {}) => {
    if (!businessId) return;
    try {
      const res = await getCxCSummary(businessId, {
        from_date: dates.from_date || null,
        to_date:   dates.to_date   || null
      });
      setSummary(res.data?.data || null);
    } catch {}
  }, [businessId]);

  const loadList = useCallback(async (f) => {
    if (!businessId) return;
    setLoading(true);
    const ef = f || filters;
    try {
      const res = await getCxCList(businessId, { ...ef, page, limit: perPage });
      setRows(res.data?.rows  || []);
      setTotal(res.data?.total || 0);
    } catch {} finally { setLoading(false); }
  }, [businessId, filters, page, perPage]);

  const reload = useCallback(() => {
    loadSummary(appliedDates);
    loadList();
  }, [loadSummary, loadList, appliedDates]);

  useEffect(() => {
    if (!businessId) return;
    getClients(businessId).then(r => setClients(r.data?.data || []));
    loadSummary();
    loadList();
  }, [businessId]);

  useEffect(() => { loadList(); }, [page, perPage]);

  const handleFiltrar = () => {
    const f = { ...filters, search: searchText };
    setFilters(f);
    setPage(1);
    const dates = { from_date: filters.from_date, to_date: filters.to_date };
    setAppliedDates(dates);
    loadList(f);
    loadSummary(dates);
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const handleExport = async (format) => {
    if (exporting) return;
    setExporting(true);
    try {
      // Cargar todos los registros con los filtros actuales (sin paginación)
      const res = await getCxCList(businessId, { ...filters, search: searchText, page: 1, limit: 9999 });
      const allData = res.data?.rows || [];

      let empresa = { name: 'PIVASAPOLLOS', ruc: '20100000001', address: 'AV. BENAVIDES NRO 6600-6606, MIRAFLORES - LIMA' };
      try {
        const er = await getEmpresaInfo(businessId);
        if (er.data?.data) empresa = er.data.data;
      } catch {}

      const args = { empresa, data: allData, usuario: null, summary };
      if (format === 'pdf')   exportPDF(args);
      if (format === 'excel') exportExcel(args);
    } catch (e) {
      console.error('Error al exportar:', e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mt-container">
      {/* HEADER */}
      <div className="mt-toolbar">
        <div className="mt-toolbar-left">
          <span className="mt-title">CUENTAS POR COBRAR — GESTIÓN DE DEUDAS DE CLIENTES</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-nuevo" style={{ background: '#475569' }} onClick={reload}>
            <FiRefreshCw style={{ fontSize: 12 }} /> Actualizar
          </button>
          <button
            className="btn-export-pdf"
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            title="Exportar PDF"
          >
            <FiFileText size={13} /> {exporting ? '...' : 'PDF'}
          </button>
          <button
            className="btn-export-excel"
            onClick={() => handleExport('excel')}
            disabled={exporting}
            title="Exportar Excel"
          >
            <FiDownload size={13} /> {exporting ? '...' : 'Excel'}
          </button>
        </div>
      </div>

      {/* KPIs */}
      {summary && (() => {
        const hasDateFilter = !!(appliedDates.from_date || appliedDates.to_date);
        const dateRef = hasDateFilter ? (() => {
          // Reformateo directo del string (sin pasar por Date/zona horaria) — `s`
          // ya es una fecha calendario pura (del filtro), no un instante UTC.
          const p = s => { if (!s) return null; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
          const f = p(appliedDates.from_date);
          const t = p(appliedDates.to_date);
          if (f && t) return `${f} — ${t}`;
          if (f)      return `Desde ${f}`;
          return `Hasta ${t}`;
        })() : null;

        return (
          <div style={{
            display: 'grid',
            gridTemplateColumns: hasDateFilter ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
            gap: 12, padding: '12px 16px 4px'
          }}>
            {hasDateFilter && (
              <KpiCard icon={<FiTrendingDown />} color="blue"
                label="Total Deuda"
                value={fmtS(summary.total_deuda)}
                sub={dateRef} />
            )}
            <KpiCard icon={<FiClock />} color="orange"
              label="Pendiente de Cobro"
              value={fmtS(summary.total_pendiente)}
              sub={`${+summary.cnt_pending + +summary.cnt_partial} activos`} />
            <KpiCard icon={<FiAlertCircle />} color="red"
              label="Deuda Vencida"
              value={fmtS(summary.total_vencido)}
              sub={`${summary.cnt_vencido} vencidos`} />
            {hasDateFilter && (
              <KpiCard icon={<FiCheckCircle />} color="green"
                label="Total Cobrado"
                value={fmtS(summary.total_cobrado)}
                sub={dateRef} />
            )}
          </div>
        );
      })()}

      {/* FILTROS */}
      <div className="si-filter-bar">
        <div className="si-filter-group">
          <ClienteFiltroCombo
            clients={clients}
            value={filters.client_id}
            onChange={id => setFilters(f => ({ ...f, client_id: id }))}
          />
          <select className="si-select" value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">Todos (sin anuladas)</option>
            <option value="con_deuda">Con Deuda (pendiente + parcial)</option>
            <option value="pending">Solo pendiente (sin abonar)</option>
            <option value="partial">Parcialmente cobrado</option>
            <option value="paid">Cobrado completo</option>
            <option value="cancelled">Anuladas (venta cancelada)</option>
          </select>
          <span className="si-label">Desde:</span>
          <input type="date" className="si-input-date" value={filters.from_date}
            onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))} />
          <span className="si-label">Hasta:</span>
          <input type="date" className="si-input-date" value={filters.to_date}
            onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#475569', cursor: 'pointer' }}>
            <input type="checkbox" checked={filters.overdue_only}
              onChange={e => setFilters(f => ({ ...f, overdue_only: e.target.checked }))} />
            Solo vencidos
          </label>
          <div className="si-search-wrap">
            <FiSearch className="si-search-icon" />
            <input className="si-search" placeholder="Cliente, N° venta..."
              value={searchText} onChange={e => setSearchText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleFiltrar()} />
          </div>
          <button className="btn-nuevo" onClick={handleFiltrar} disabled={loading}>
            <FiRefreshCw style={{ fontSize: 12 }} /> Filtrar
          </button>
        </div>
      </div>

      {/* RESUMEN BARRA */}
      <div className="mt-export-bar" style={{ justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: '#64748b' }}>
          {loading ? 'Cargando...' : `${total} registro(s)`}
        </span>
        <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 700 }}>
          Total pendiente visible: {fmtS(rows.filter(r => r.status !== 'paid').reduce((s, r) => s + parseFloat(r.restante || 0), 0))}
        </span>
        <div className="mt-pagination">
          <button onClick={() => setPage(1)} disabled={page === 1}>|&lt;</button>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>&lt;</button>
          <span className="pg-current">{page}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>&gt;</button>
          <button onClick={() => setPage(totalPages)} disabled={page >= totalPages}>&gt;|</button>
          <span className="pg-info">de {totalPages}</span>
          <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}>
            {[25, 50, 100].map(n => <option key={n} value={n}>{n}/pág</option>)}
          </select>
        </div>
      </div>

      {/* TABLA — agrupada por cliente, desglosable */}
      <div className="mt-table-wrap">
        {loading ? (
          <table className="mt-table"><tbody>
            <tr className="mt-empty"><td>Cargando...</td></tr>
          </tbody></table>
        ) : groups.length === 0 ? (
          <table className="mt-table"><tbody>
            <tr className="mt-empty"><td>Sin registros para los filtros seleccionados</td></tr>
          </tbody></table>
        ) : (
          <table className="mt-table mt-table-grouped">
            <thead>
              <tr className="mt-head-row">
                <th style={{ width: 30 }}></th>
                <th>Cliente</th>
                <th>DNI/RUC</th>
                <th style={{ textAlign: 'right' }}>N° Cuotas</th>
                <th style={{ textAlign: 'right' }}>Monto Total</th>
                <th style={{ textAlign: 'right' }}>Cobrado</th>
                <th style={{ textAlign: 'right' }}>Restante</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => {
                const isOpen = expandedClients.has(g.key);
                return (
                  <React.Fragment key={g.key}>
                    <tr
                      className="mt-group-row"
                      onClick={() => toggleClient(g.key)}
                      title="Click para desglosar"
                    >
                      <td>{isOpen ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}</td>
                      <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={g.client_name}>
                        {g.client_name}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11.5 }}>{g.client_document}</td>
                      <td style={{ textAlign: 'right' }}>{g.count}</td>
                      <td style={{ textAlign: 'right' }}>{fmtS(g.total_monto)}</td>
                      <td style={{ textAlign: 'right', color: '#16a34a' }}>{fmtS(g.total_cobrado)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: g.total_restante > 0 ? '#dc2626' : '#16a34a' }}>
                        {fmtS(g.total_restante)}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="mt-group-detail">
                        <td colSpan={7}>
                          <table className="mt-table mt-table-nested">
                            <thead>{CXC_ROWS_HEAD}</thead>
                            <tbody>
                              <CxCRows rows={g.rows} onCobrar={setCobroModal} />
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* MODAL DE COBRO */}
      {cobroModal && (
        <ModalCobro
          arId={cobroModal}
          businessId={businessId}
          onClose={() => setCobroModal(null)}
          onCobrado={() => { setCobroModal(null); reload(); }}
        />
      )}
    </div>
  );
}
